<template>
    <div v-show="groups.length" ref="scroll" class="review-grid__scroll" tabindex="0"
        aria-label="Таблица сотрудников и дней, прокручивается по горизонтали и вертикали"
        @scroll.passive="scrolled" @focusin="focusChanged" @focusout="focusChanged" @keydown="navigate">
        <table :style="{'--review-days': model.columns.length}" :aria-rowcount="items.length + 1">
            <thead><tr><th class="name">Фамилия И.О.</th><th class="object">Объект / табель</th>
                <th v-for="column in model.columns" :key="column.date" class="day" :class="{weekend: column.weekend}" :title="column.date">{{ column.label }}<small>{{ column.weekday }}</small></th>
                <th class="total" title="Сумма эквивалентных дней по табелям">Дни</th><th class="total">Часы</th><th class="author">Подал / изменил</th><th class="ktu">КТУ</th>
            </tr></thead>
            <transition-group ref="body" tag="tbody" :css="false" name="review-rows">
                <template v-for="item in segments">
                    <tr v-if="item.type === 'spacer'" :key="item.key" class="review-grid__spacer" aria-hidden="true"><td :colspan="model.columns.length + 6" :style="{height: `${item.height}px`}"></td></tr>
                    <timesheet-review-row v-else :key="item.key" :item="model.rowView(item)" :aria-rowindex="indices.get(item.key) + 2"
                        :selected="Boolean(item.row && selectedObject && String(model.source(item.row).building_id) === selectedObject)"
                        @details="$emit('details', $event)" @open-employee="$emit('open-employee', $event)" />
                </template>
            </transition-group>
        </table>
    </div>
</template>
<script>
import TimesheetReviewRow from './TimesheetReviewRow.vue';
import {retainedWindow, segmentsInRange, rowAtOffset} from '@/utils/timesheetVirtualRows';

export default {
    name: 'TimesheetReviewTable',
    components: {TimesheetReviewRow},
    props: {model: Object, groups: Array, selectedObject: String, position: Object},
    data: () => ({heights: Object.freeze({}), estimates: Object.freeze({source: 36, total: 26, plain: 26}), windowRange: null, pinnedKey: null}),
    computed: {
        items() { return this.model.items(this.groups); },
        indices() { return new Map(this.items.map((item, index) => [item.key, index])); },
        offsets() {
            const result = [0];
            this.items.forEach(item => result.push(result[result.length - 1] + (this.heights[item.key] || this.estimates[this.rowType(item)])));
            return Object.freeze(result);
        },
        segments() { return segmentsInRange(this.items, this.offsets, this.windowRange || retainedWindow(this.offsets, 0, 500), this.pinnedKey); }
    },
    watch: {
        groups() {
            if (this.pinnedKey && !this.indices.has(this.pinnedKey)) this.pinnedKey = null;
            this._pendingAnchor = this._anchor || {key: this.items[0] && this.items[0].key, offset: 0};
            const index = this.indices.get(this._pendingAnchor.key), geometry = this.geometry();
            const top = index == null ? 0 : this.offsets[index] + this._pendingAnchor.offset;
            this.windowRange = retainedWindow(this.offsets, top, geometry ? geometry.height : 500);
            // Keep the anchor until the new spacers are patched. Assigning a far
            // scrollTop against the old, shorter table would silently clamp it.
            this.$nextTick(() => { this.restoreAnchor(); this.schedule(); });
            this.schedule();
        }
    },
    mounted() {
        if (typeof ResizeObserver !== 'undefined') {
            this._observer = new ResizeObserver(this.resized);
            this._observer.observe(this.$refs.scroll);
        }
        window.addEventListener('resize', this.resized);
        window.addEventListener('scroll', this.rememberScroll, {passive: true});
        window.addEventListener('pagehide', this.flushPosition);
        this.$nextTick(() => {
            const element = this.$refs.scroll;
            if (this.position) { element.scrollTop = this.position.scrollTop || 0; element.scrollLeft = this.position.scrollLeft || 0; }
            this.measure();
        });
    },
    beforeUpdate() {
        const control = document.activeElement;
        this._focusedControl = this.$el.contains(control) ? control : null;
    },
    updated() {
        const control = this._focusedControl;
        this._focusedControl = null;
        if (control && control.isConnected && document.activeElement === document.body) control.focus({preventScroll: true});
        this.schedule();
    },
    beforeDestroy() {
        this.flushPosition();
        cancelAnimationFrame(this._frame);
        clearTimeout(this._focusTimer);
        if (this._observer) this._observer.disconnect();
        window.removeEventListener('resize', this.resized);
        window.removeEventListener('scroll', this.rememberScroll);
        window.removeEventListener('pagehide', this.flushPosition);
    },
    methods: {
        rowType(item) { return !item.row ? 'total' : this.model.source(item.row).author_id ? 'source' : 'plain'; },
        currentPosition() {
            const element = this.$refs.scroll;
            return element ? {scrollTop: element.scrollTop, scrollLeft: element.scrollLeft, pageScrollTop: window.scrollY} : null;
        },
        rememberScroll() {
            clearTimeout(this._positionTimer);
            this._positionTimer = setTimeout(this.flushPosition, 200);
        },
        flushPosition() {
            clearTimeout(this._positionTimer); this._positionTimer = null;
            const position = this.currentPosition();
            if (position) this.$emit('position', position);
        },
        scrolled() { this.schedule(); this.rememberScroll(); },
        schedule() {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => { this._frame = null; this.measure(); });
        },
        resized() {
            const element = this.$refs.scroll;
            if (element && this._width !== element.clientWidth) {
                this._width = element.clientWidth;
                this.heights = Object.freeze({});
                this._pendingAnchor = this._anchor;
            }
            this.schedule();
        },
        geometry() {
            const viewport = this.$refs.scroll, body = this.$refs.body && this.$refs.body.$el;
            if (!viewport || !body || !viewport.clientHeight) return null;
            const header = body.parentElement.tHead.getBoundingClientRect().height;
            const offset = body.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop;
            return {viewport, body, header, offset, height: Math.max(30, viewport.clientHeight - header)};
        },
        restoreAnchor() {
            const anchor = this._pendingAnchor, geometry = this.geometry();
            if (!anchor || !geometry) return;
            this._pendingAnchor = null;
            const index = this.indices.get(anchor.key);
            geometry.viewport.scrollTop = index == null ? 0 : Math.max(0, geometry.offset - geometry.header + this.offsets[index] + anchor.offset);
            const top = Math.max(0, geometry.viewport.scrollTop + geometry.header - geometry.offset);
            this.windowRange = retainedWindow(this.offsets, top, geometry.height);
        },
        measure() {
            let geometry = this.geometry();
            if (!geometry || !this.items.length) return;
            const {viewport, body, header, offset, height} = geometry;
            let top = Math.max(0, viewport.scrollTop + header - offset);
            const first = rowAtOffset(this.offsets, top);
            const anchor = {key: this.items[first].key, offset: top - this.offsets[first]};
            const measured = {}, estimates = {...this.estimates};
            Array.from(body.children).forEach(row => {
                const key = row.getAttribute('data-virtual-key'), index = this.indices.get(key);
                if (index == null) return;
                const value = row.getBoundingClientRect().height;
                if (value > 0 && Math.abs((this.heights[key] || 0) - value) > .5) measured[key] = value;
                if (value > 0) estimates[this.rowType(this.items[index])] = value;
            });
            const estimatesChanged = Object.keys(estimates).some(key => Math.abs(estimates[key] - this.estimates[key]) > .5);
            if (Object.keys(measured).length || estimatesChanged) {
                this.heights = Object.freeze({...this.heights, ...measured});
                if (estimatesChanged) this.estimates = Object.freeze(estimates);
                this._pendingAnchor = this._pendingAnchor || anchor;
            }
            this.restoreAnchor();
            top = Math.max(0, viewport.scrollTop + header - offset);
            if (this._lastTop != null && Math.abs(viewport.scrollTop - this._lastTop) > .5) this._direction = viewport.scrollTop > this._lastTop ? 1 : -1;
            this._lastTop = viewport.scrollTop;
            const range = retainedWindow(this.offsets, top, height, this._direction || 1, this.windowRange);
            if (!this.windowRange || range.start !== this.windowRange.start || range.end !== this.windowRange.end) this.windowRange = range;
            const index = rowAtOffset(this.offsets, top);
            this._anchor = {key: this.items[index].key, offset: top - this.offsets[index]};
        },
        focusChanged() {
            clearTimeout(this._focusTimer);
            this._focusTimer = setTimeout(() => {
                const control = document.activeElement;
                const row = this.$el.contains(control) && control.closest('[data-virtual-key]');
                this.pinnedKey = row ? row.getAttribute('data-virtual-key') : null;
            }, 0);
        },
        async navigate(event) {
            // Native Tab still traverses every logical row, including a gap beside
            // a pinned row. Ordinary clicks/Enter/Space keep native button behavior.
            if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) return;
            const row = event.target.closest('[data-virtual-key]');
            if (!row) return;
            const buttons = Array.from(row.querySelectorAll('button'));
            if (event.target !== buttons[event.shiftKey ? 0 : buttons.length - 1]) return;
            const index = this.indices.get(row.getAttribute('data-virtual-key')) + (event.shiftKey ? -1 : 1);
            const item = this.items[index];
            if (!item) return;
            event.preventDefault();
            this.pinnedKey = item.key;
            const geometry = this.geometry();
            geometry.viewport.scrollTop = Math.max(0, geometry.offset - geometry.header + this.offsets[index]);
            this.measure();
            await this.$nextTick();
            const next = Array.from(geometry.body.children).find(element => element.getAttribute('data-virtual-key') === item.key);
            const controls = next && next.querySelectorAll('button');
            if (controls && controls.length) controls[event.shiftKey ? controls.length - 1 : 0].focus({preventScroll: true});
        }
    }
};
</script>
